import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

export interface InsightsData {
  marketViability: {
    currentTrends: string[];
    assignmentTypes: Array<{ sector: string; type: string; frequency: string }>;
    contractPatterns: string[];
  };
  demandedCompetencies: {
    technical: string[];
    business: string[];
    sectorSpecific: string[];
    emerging: string[];
  };
  sectorInsights: {
    private: string[];
    public: string[];
    energy: string[];
  };
  marketPrognosis: {
    shortTerm: string[];
    longTerm: string[];
  };
  strategicRecommendations: {
    immediateActions: string[];
    investmentPriorities: string[];
    competitiveAdvantages: string[];
    riskMitigation: string[];
  };
  summary: string;
  sources: string[];
}

export interface InsightsRow {
  id: string;
  tenantId: string;
  type: string;
  data: InsightsData;
  inputData: Record<string, unknown> | null;
  createdBy: string | null;
  createdAt: string;
}

export interface InsightsHistoryEntry {
  id: string;
  type: string;
  createdAt: string;
  createdBy: string | null;
  summary: string;
}

export function useMarketInsights(type: 'internal' | 'external') {
  return useQuery({
    queryKey: ['market-insights', type],
    queryFn: () => apiFetch<InsightsRow | null>(`/admin/market-insights?type=${type}`)
  });
}

export function useInsightsById(id: string | null) {
  return useQuery({
    queryKey: ['market-insights', id],
    queryFn: () => apiFetch<InsightsRow>(`/admin/market-insights/${id}`),
    enabled: !!id
  });
}

export function useInsightsHistory() {
  return useQuery({
    queryKey: ['market-insights-history'],
    queryFn: () => apiFetch<InsightsHistoryEntry[]>('/admin/market-insights/history')
  });
}

export function useGenerateInsights() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type }: { type: 'internal' | 'external' }) =>
      apiFetch<InsightsRow>('/admin/market-insights', {
        method: 'POST',
        body: JSON.stringify({ type })
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['market-insights', variables.type] });
      queryClient.invalidateQueries({ queryKey: ['market-insights-history'] });
    }
  });
}

export function useDeleteInsight() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<null>(`/admin/market-insights/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-insights-history'] });
      queryClient.invalidateQueries({ queryKey: ['market-insights'] });
    }
  });
}
